import AuthService from '@/shared/services/auth.service';
import { STORAGE_KEYS } from '@/config/api.config';
import authStorage from '@/shared/services/auth-storage.service';
import axios from 'axios';

const resolveRole = (user, fallbackRole) => {
  if (!user) {
    return fallbackRole ?? null;
  }

  return (
    user.roleName ||
    user.role ||
    user.role?.name ||
    user.role_name ||
    fallbackRole ||
    null
  );
};

const persistAuthData = ({ access_token, refresh_token, user, role }) => {
  if (access_token) {
    authStorage.setItem(STORAGE_KEYS.JWT, access_token);
  }

  if (refresh_token) {
    authStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refresh_token);
  }

  if (user) {
    authStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  }

  if (role) {
    authStorage.setItem(STORAGE_KEYS.ROLE, role);
  }
};

const clearPersistedAuth = () => {
  authStorage.clearSession();
};

const actions = {
  async login({ commit }, { email, password }) {
    commit('SET_STATUS', 'loading');

    try {
      const response = await AuthService.login(email, password);
      const role = resolveRole(response.user, response.role);

      commit('SET_TOKENS', {
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
      });
      commit('SET_USER', response.user ?? null);
      commit('SET_ROLE', role);
      commit('SET_STATUS', 'success');

      persistAuthData({ ...response, role });

      return response;
    } catch (error) {
      commit('SET_STATUS', 'error');
      throw error;
    }
  },

  async logout({ commit }) {
    try {
      await AuthService.logout();
    } catch (error) {
      // Ignore logout call failures but still clear client state
    } finally {
      commit('CLEAR_AUTH_STATE');
      commit('SET_STATUS', 'idle');
      clearPersistedAuth();
    }
  },

  logoutLocal({ commit }) {
    commit('CLEAR_AUTH_STATE');
    commit('SET_STATUS', 'idle');
    clearPersistedAuth();
    delete axios.defaults.headers.common['Authorization'];
  },

  async refreshToken({ state, commit }) {
    const existingRefreshToken =
      state.tokens.refresh || authStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

    if (!existingRefreshToken) {
      throw new Error('Missing refresh token');
    }

    const response = await AuthService.refreshToken(existingRefreshToken);
    const role = resolveRole(response.user, state.role);

    commit('SET_TOKENS', {
      accessToken: response.access_token,
      refreshToken: response.refresh_token ?? existingRefreshToken,
    });
    commit('SET_USER', response.user ?? state.user);
    commit('SET_ROLE', role);

    persistAuthData({ ...response, role });

    return response;
  },

  /**
   * Le mot de passe temporaire vient d'etre remplace. Les jetons restent
   * valables - c'est le meme compte - mais l'utilisateur stocke porte encore
   * le drapeau, et c'est lui que lisent le garde de navigation et les ecrans.
   */
  passwordChanged({ commit, state }) {
    const user = { ...(state.user ?? {}), mustChangePassword: false };

    commit('SET_USER', user);
    persistAuthData({ user });
  },

  restoreSession({ commit, state }) {
    if (state.sessionRestored) {
      return;
    }

    const accessToken = authStorage.getItem(STORAGE_KEYS.JWT);
    const refreshToken = authStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    const userString = authStorage.getItem(STORAGE_KEYS.USER);
    const storedRole = authStorage.getItem(STORAGE_KEYS.ROLE);

    const user = userString ? JSON.parse(userString) : null;
    const role = resolveRole(user, storedRole);

    commit('SET_TOKENS', {
      accessToken,
      refreshToken,
    });
    commit('SET_USER', user);
    commit('SET_ROLE', role);
    commit('SET_STATUS', accessToken ? 'success' : 'idle');
    commit('SET_SESSION_RESTORED', true);
  },
};

export default actions;
